/**
 * ExpertiseModal.jsx — Formulaire Expertise (parité FrmExpert.vb)
 *
 * Fonctionnalités :
 *   • Liste déroulante des expertises du dossier (cboListExp legacy)
 *   • CRUD Expertise (INSERT / UPDATE / DELETE)
 *   • Validations date ajoutées (parité v72) :
 *       - DateDemande ≤ today
 *       - DateAutorise requise si TypeAutorisation ∈ {1,2}
 *       - DateAutorise ≥ DateDemande et ≤ today
 *   • Logique CheckBtnRadio legacy : copie coûts prévus → autorisés si "Tel que"
 *
 * Backend : /api/expertise (POST/PUT/DELETE) + /api/expertise/by-dossier
 */
import React, { useEffect, useState, useCallback } from 'react';
import { todayLocal } from '../utils/dateLocal';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const _empty = {
  Pk_Expertise: '', NoRef: '', NoDossier: '', NumAvo: '', CodeAvoSign: '',
  DateDemande: todayLocal(),
  NomExp: '', SpecialiteExp: '', NatureExp: '', MotifDemande: '',
  HrePrevu: 0, TauxHrePrevu: 0, ForfaitPrevu: 0, TemoignPrevu: 0,
  DateAutorise: '', HeureAut: 0, TauxHreAut: 0, ForfaitAut: 0, TemoignAut: 0,
  TypeAutorisation: 0, Admissibilite: 0, CommentaireExp: '',
};

const inpStyle = {
  height: 30, padding: '4px 8px', border: '1px solid #9db3d6',
  background: '#fff', fontFamily: 'Segoe UI, Tahoma, sans-serif',
  fontSize: 14, width: '100%',
};
const labelStyle = { fontSize: 13, fontFamily: 'Segoe UI, Tahoma, sans-serif' };
const money = (n) => (n === null || n === undefined || n === '' ? ''
  : parseFloat(n).toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' }));

// Champ montant en dollars canadiens : affiche le format CAD (ex. « 125,00 $ »)
// hors focus, et la valeur numérique brute en édition. Pas de flèches (type texte).
const MoneyInput = ({ value, onChange, style, testId, disabled }) => {
  const [focused, setFocused] = useState(false);
  const [raw, setRaw] = useState('');
  const display = focused ? raw : money(value || 0);
  return (
    <input type="text" inputMode="decimal" style={style} data-testid={testId} disabled={disabled}
           value={display}
           onFocus={() => { setRaw(value ? String(value) : ''); setFocused(true); }}
           onChange={e => {
             setRaw(e.target.value);
             const n = parseFloat(String(e.target.value).replace(/\s/g, '').replace(',', '.').replace(/[^0-9.]/g, ''));
             onChange(isNaN(n) ? 0 : n);
           }}
           onBlur={() => setFocused(false)} />
  );
};

const ExpertiseModal = ({ open, onClose, dossier }) => {
  const [list, setList] = useState([]);
  const [avocats, setAvocats] = useState([]);
  const [form, setForm] = useState(_empty);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const pkDaj = ((dossier?.no_seq_daj || dossier?.no_sequence || '') + '').replace(/-/g, '');

  const reloadList = useCallback(async () => {
    if (!pkDaj) return;
    try {
      const { data } = await axios.get(`${API}/expertise/by-dossier`,
        { params: { pk_daj: pkDaj } });
      setList(data.items || []);
    } catch { setList([]); }
  }, [pkDaj]);

  // Charge la liste des avocats signataires (code 'A*' via prefix=A —
  // parité VB.NET où le combo cboAvoSign ne liste QUE les codes 'A*').
  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/reference/avocats`, { params: { prefix: 'A' } })
      .then(r => setAvocats(r.data || []))
      .catch(() => setAvocats([]));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setError('');
    setForm(p => ({ ..._empty,
      NoReg: dossier?.no_region || '', NoBur: dossier?.no_bureau || '',
      NoRef: dossier?.no_reference || '',
      NoDossier: dossier?.no_dossier || '',
      NoClient: dossier?.no_client || '',
      NumAvo: dossier?.dossier_confie_a || '',
      // Défaut : l'avocat au dossier devient signataire (modifiable ensuite).
      // Parité v73 : CodeAvoSign pré-rempli avec `dossier_confie_a` — c'est
      // l'usage typique (l'avocat qui suit le dossier signe l'expertise),
      // mais l'utilisateur peut sélectionner un autre avocat au besoin.
      CodeAvoSign: (dossier?.dossier_confie_a || '').trim() || '',
      Admissibilite: dossier?.avec_contribution ? 1
        : (dossier?.gratuit ? 2 : 0),
    }));
    reloadList();
  }, [open, dossier, reloadList]);

  // Réinitialise le formulaire à un état vierge tout en préservant le
  // contexte du dossier ouvert (avocat au dossier, région, bureau, etc.)
  // — comportement legacy `InitExpert` VB.NET qui ne vide QUE les champs
  // spécifiques à l'expertise, pas les champs hérités du dossier.
  const resetToDossierContext = useCallback(() => ({ ..._empty,
    NoReg: dossier?.no_region || '', NoBur: dossier?.no_bureau || '',
    NoRef: dossier?.no_reference || '',
    NoDossier: dossier?.no_dossier || '',
    NoClient: dossier?.no_client || '',
    NumAvo: dossier?.dossier_confie_a || '',
    CodeAvoSign: (dossier?.dossier_confie_a || '').trim() || '',
    Admissibilite: dossier?.avec_contribution ? 1
      : (dossier?.gratuit ? 2 : 0),
  }), [dossier]);

  const loadExpertise = async (pk) => {
    setError('');
    if (!pk) { setForm(resetToDossierContext()); return; }
    const { data } = await axios.get(`${API}/expertise/${pk}`);
    setForm(p => ({ ...p, ...data }));
  };

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // Logique legacy CheckBtnRadio : copie prévu → autorisé si "Tel que demandé"
  const onTypeChange = (val) => {
    setForm(p => {
      const next = { ...p, TypeAutorisation: val };
      if (val === 1) {
        next.HeureAut = p.HrePrevu; next.TauxHreAut = p.TauxHrePrevu;
        next.ForfaitAut = p.ForfaitPrevu; next.TemoignAut = p.TemoignPrevu;
        next.DateAutorise = todayLocal();
      } else if (val === 2) {
        next.HeureAut = 0; next.TauxHreAut = 0;
        next.ForfaitAut = 0; next.TemoignAut = 0;
        next.DateAutorise = todayLocal();
      } else {
        next.DateAutorise = '';
      }
      return next;
    });
  };

  const doSave = async () => {
    setError(''); setSaving(true);
    try {
      const payload = { ...form,
        Pk_Daj: pkDaj,
        NoReg: form.NoReg || dossier?.no_region || '',
        NoBur: form.NoBur || dossier?.no_bureau || '',
        NoRef: form.NoRef || dossier?.no_reference || '',
        NoClient: form.NoClient || dossier?.no_client || '',
      };
      const url = form.Pk_Expertise
        ? `${API}/expertise/${form.Pk_Expertise}`
        : `${API}/expertise`;
      const method = form.Pk_Expertise ? 'put' : 'post';
      const { data } = await axios[method](url, payload);
      setForm(p => ({ ...p, Pk_Expertise: data.Pk_Expertise }));
      await reloadList();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    if (!form.Pk_Expertise) return;
    if (!window.confirm('Supprimer cette expertise ?')) return;
    await axios.delete(`${API}/expertise/${form.Pk_Expertise}`);
    setForm(resetToDossierContext()); await reloadList();
  };

  const totalPrev = ((form.HrePrevu || 0) * (form.TauxHrePrevu || 0));
  const totalAuto = ((form.HeureAut || 0) * (form.TauxHreAut || 0));

  if (!open) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
                  zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
         data-testid="expertise-modal">
      <div style={{ background: '#fff', border: '2px solid #0033A0',
                    width: 720, maxHeight: '92vh', overflow: 'auto',
                    fontFamily: 'Segoe UI, Tahoma, sans-serif', fontSize: 13 }}>
        <div style={{ background: '#0033A0',
                      color: '#fff', padding: '10px 14px', fontWeight: 'bold', fontSize: 15 }}>
          Expertise
        </div>
        <div style={{ padding: 14 }}>
          {/* Liste expertises existantes */}
          <div style={{ marginBottom: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
            <label style={labelStyle}>Liste des expertises :</label>
            <select style={{ ...inpStyle, flex: 1 }}
                    value={form.Pk_Expertise}
                    onChange={e => loadExpertise(e.target.value)}
                    data-testid="expertise-liste">
              <option value="">-- Nouvelle expertise --</option>
              {list.map(e => (
                <option key={e.Pk_Expertise} value={e.Pk_Expertise}>
                  {(e.DateDemande || '').slice(0, 10)} — {e.NomExp || '(sans nom)'}
                </option>
              ))}
            </select>
          </div>

          {/* Boutons (remontés en haut) */}
          <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
            <button onClick={() => { setError(''); setForm(resetToDossierContext()); }} className="win-btn"
                    data-testid="expertise-btn-new">Nouveau</button>
            <button onClick={doDelete} className="win-btn"
                    disabled={!form.Pk_Expertise}
                    data-testid="expertise-btn-delete">Supprimer</button>
            <button onClick={doSave} className="win-btn"
                    disabled={saving}
                    data-testid="expertise-btn-save">
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </button>
            <button onClick={onClose} className="win-btn"
                    data-testid="expertise-btn-close">Fermer</button>
          </div>

          {/* Message d'erreur */}
          {error && (
            <div data-testid="expertise-error"
                 style={{ marginBottom: 8, padding: 6, background: '#fee', color: '#c00',
                          border: '1px solid #c00', whiteSpace: 'pre-line', fontSize: 13 }}>
              {error}
            </div>
          )}

          {/* Date + Expert */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div>
              <div style={labelStyle}>Date demande <span style={{color:'red'}}>*</span></div>
              <input type="date" style={inpStyle}
                     max={todayLocal()}
                     value={form.DateDemande || ''}
                     onChange={e => set('DateDemande', e.target.value)}
                     data-testid="expertise-date-demande" />
            </div>
            <div>
              <div style={labelStyle}>Nom de l&apos;expert <span style={{color:'red'}}>*</span></div>
              <input style={inpStyle} maxLength={50}
                     value={form.NomExp || ''}
                     onChange={e => set('NomExp', e.target.value)}
                     data-testid="expertise-nom-exp" />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 6 }}>
            <div>
              <div style={labelStyle}>Spécialité</div>
              <input style={inpStyle} maxLength={50} value={form.SpecialiteExp || ''}
                     onChange={e => set('SpecialiteExp', e.target.value)} />
            </div>
            <div>
              <div style={labelStyle}>Nature de l&apos;expertise</div>
              <input style={inpStyle} maxLength={50} value={form.NatureExp || ''}
                     onChange={e => set('NatureExp', e.target.value)} />
            </div>
          </div>

          <div style={{ marginTop: 6 }}>
            <div style={labelStyle}>Motif de la demande</div>
            <textarea style={{ ...inpStyle, height: 40, resize: 'vertical' }}
                      maxLength={200} value={form.MotifDemande || ''}
                      onChange={e => set('MotifDemande', e.target.value)} />
          </div>

          {/* Section Coûts prévus */}
          <fieldset style={{ marginTop: 8, padding: 6, border: '1px solid #9db3d6', background: '#f0f4fa' }}>
            <legend style={{ ...labelStyle, fontWeight: 'bold', color: '#0033A0' }}>Coûts prévus</legend>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 6 }}>
              <div><div style={labelStyle}>Nb heures</div>
                <input type="number" min="0" className="no-spinner" style={inpStyle}
                       value={form.HrePrevu || 0}
                       onChange={e => set('HrePrevu', parseInt(e.target.value || 0, 10))} />
              </div>
              <div><div style={labelStyle}>Taux/hre</div>
                <MoneyInput value={form.TauxHrePrevu} style={inpStyle}
                            onChange={v => set('TauxHrePrevu', v)} testId="expertise-taux-prevu" />
              </div>
              <div><div style={labelStyle}>Forfait</div>
                <MoneyInput value={form.ForfaitPrevu} style={inpStyle}
                            onChange={v => set('ForfaitPrevu', v)} testId="expertise-forfait-prevu" />
              </div>
              <div><div style={labelStyle}>Témoignage</div>
                <input type="number" min="0" step="0.01" className="no-spinner" style={inpStyle}
                       value={form.TemoignPrevu || 0}
                       onChange={e => set('TemoignPrevu', parseFloat(e.target.value || 0))} />
              </div>
            </div>
            <div style={{ marginTop: 4, textAlign: 'right', fontSize: 12, color: '#555' }}>
              Total prévu : <b>{money(totalPrev)}</b>
            </div>
          </fieldset>

          {/* Radios Autorisation */}
          <div style={{ marginTop: 8, padding: 6, border: '1px solid #9db3d6', background: '#f0f4fa' }}>
            <div style={{ ...labelStyle, fontWeight: 'bold' }}>Autorisation</div>
            <label style={{ marginRight: 12 }}>
              <input type="radio" name="type-aut" checked={form.TypeAutorisation === 0}
                     onChange={() => onTypeChange(0)} /> Pas autorisé
            </label>
            <label style={{ marginRight: 12 }}>
              <input type="radio" name="type-aut" checked={form.TypeAutorisation === 1}
                     onChange={() => onTypeChange(1)}
                     data-testid="expertise-rad-telque" /> Tel que demandé
            </label>
            <label>
              <input type="radio" name="type-aut" checked={form.TypeAutorisation === 2}
                     onChange={() => onTypeChange(2)}
                     data-testid="expertise-rad-avecchg" /> Avec changement
            </label>
          </div>

          {/* Section Coûts autorisés (visible si TypeAutorisation != 0) */}
          {form.TypeAutorisation !== 0 && (
            <fieldset style={{ marginTop: 6, padding: 6, border: '1px solid #9db3d6', background: '#fff' }}>
              <legend style={{ ...labelStyle, fontWeight: 'bold', color: '#0033A0' }}>Coûts autorisés</legend>
              <div style={{ marginBottom: 4 }}>
                <div style={labelStyle}>Date d&apos;autorisation <span style={{color:'red'}}>*</span></div>
                <input type="date" style={{...inpStyle, width: 140}}
                       max={todayLocal()}
                       min={form.DateDemande || ''}
                       value={form.DateAutorise || ''}
                       onChange={e => set('DateAutorise', e.target.value)}
                       data-testid="expertise-date-autorise" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 6 }}>
                <div><div style={labelStyle}>Nb heures</div>
                  <input type="number" min="0" className="no-spinner" style={inpStyle}
                         value={form.HeureAut || 0}
                         onChange={e => set('HeureAut', parseInt(e.target.value || 0, 10))} />
                </div>
                <div><div style={labelStyle}>Taux/hre</div>
                  <MoneyInput value={form.TauxHreAut} style={inpStyle}
                              onChange={v => set('TauxHreAut', v)} testId="expertise-taux-aut" />
                </div>
                <div><div style={labelStyle}>Forfait</div>
                  <MoneyInput value={form.ForfaitAut} style={inpStyle}
                              onChange={v => set('ForfaitAut', v)} testId="expertise-forfait-aut" />
                </div>
                <div><div style={labelStyle}>Témoignage</div>
                  <input type="number" min="0" step="0.01" className="no-spinner" style={inpStyle}
                         value={form.TemoignAut || 0}
                         onChange={e => set('TemoignAut', parseFloat(e.target.value || 0))} />
                </div>
              </div>
              <div style={{ marginTop: 4, textAlign: 'right', fontSize: 12, color: '#555' }}>
                Total autorisé : <b>{money(totalAuto)}</b>
              </div>
            </fieldset>
          )}

          {/* Avocat signataire — combo alimenté par /reference/avocats?prefix=A
              (même source que la ComboBox `cboAvoSign` du formulaire principal
              Service tab). Défaut = dossier.dossier_confie_a, modifiable. */}
          <div style={{ marginTop: 6 }}>
            <div style={labelStyle}>Code avocat signataire</div>
            <select style={{...inpStyle, width: 300}}
                    value={form.CodeAvoSign || ''}
                    onChange={e => set('CodeAvoSign', e.target.value)}
                    data-testid="expertise-avo-sign">
              <option value=""></option>
              {avocats.map(a => (
                <option key={a.code} value={a.code}>
                  {a.code} — {a.label}
                </option>
              ))}
            </select>
          </div>

          {/* Commentaire libre (facultatif) — colonne CommentaireExp VARCHAR(MAX) */}
          <div style={{ marginTop: 6 }}>
            <div style={labelStyle}>Commentaire</div>
            <textarea style={{ ...inpStyle, height: 56, resize: 'vertical' }}
                      value={form.CommentaireExp || ''}
                      onChange={e => set('CommentaireExp', e.target.value)}
                      placeholder="Commentaire (facultatif)"
                      data-testid="expertise-commentaire" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpertiseModal;
